import * as d3 from "d3";
import Config from "../config";
import { Deployment } from "../domain/deployment";
import { OrganizationR, ResponseR, Year } from "../domain/organization";
import { RegionR } from "../domain/region";
import { memoize } from "../lib/memoize";
import { mkRoot, Root } from "./Root";

export type Resource<T> = REmpty | RLoading | RFailed | RDone<T>;

interface REmpty {
  type: "REmpty";
}
export const REmpty = (): Resource<any> => ({ type: "REmpty" });
interface RLoading {
  type: "RLoading";
}
export const RLoading = (): Resource<any> => ({ type: "RLoading" });
interface RFailed {
  type: "RFailed";
  error: string;
}
export const RFailed = (error: string): Resource<any> => ({ type: "RFailed", error });
interface RDone<T> {
  type: "RDone";
  data: T;
}
export const RDone = <T>(data: T): Resource<T> => ({ type: "RDone", data });

export interface Meta {
  years: Year[];
  regions: RegionR[];
  organizations: OrganizationR[];
}

export interface AtlasH {
  isLoading: boolean;

  meta: Resource<Meta>;
  fetchMeta(): Promise<unknown>;

  responses: { [year: string]: Resource<ResponseR[]> };
  fetchResponses(year: Year): Promise<unknown>;

  allResponses: ResponseR[];

  root: undefined | Root;

  fetchData(numYears: number): Promise<unknown>;
}

class AtlasHImpl implements AtlasH {
  private deployment: Deployment;
  private locale: string;

  constructor(deployment: Deployment, locale: string) {
    this.deployment = deployment;
    this.locale = locale;
  }

  get isLoading(): boolean {
    const anyResponsesLoading = () => {
      for (const year in this.responses) {
        if (this.responses[year].type !== "RDone") {
          return true;
        }
      }
      return false;
    };

    return this.meta.type !== "RDone" || anyResponsesLoading();
  }

  meta: Resource<Meta> = REmpty();
  fetchMeta = async () => {
    try {
      const [years, regions, organizations] = await Promise.all([
        new Promise<Year[]>((resolve, reject) => {
          d3.json(`${Config.apiBasePath(this.deployment)}/years.json`, (err, data) =>
            err ? reject(err) : resolve(data),
          );
        }),
        new Promise<RegionR[]>((resolve, reject) => {
          d3.csv(`${Config.apiBasePath(this.deployment)}/regions.csv`, (err: any, data: any) =>
            err ? reject(err) : resolve(data),
          );
        }),
        new Promise<OrganizationR[]>((resolve, reject) => {
          d3.csv(
            `${Config.apiBasePath(this.deployment)}/organization_data.${this.locale}.csv`,
            (err: any, data: any) => (err ? reject(err) : resolve(data)),
          );
        }),
      ]);

      this.meta = RDone({ years, regions, organizations });
    } catch (err: any) {
      this.meta = RFailed(err);
      return;
    }
  };

  responses: { [year: string]: Resource<ResponseR[]> } = {};
  fetchResponses = async (year: Year) => {
    if (this.responses[year] === undefined) {
      this.responses = { ...this.responses, [year]: RLoading() };

      try {
        const responses = await new Promise<ResponseR[]>((resolve, reject) => {
          d3.csv(`${Config.apiBasePath(this.deployment)}/survey_data/${year}.csv`, (err: any, data: any) => {
            err ? reject(err) : resolve(data);
          });
        });

        this.responses = { ...this.responses, [year]: RDone(responses) };
        return {};
      } catch (err: any) {
        this.responses = { ...this.responses, [year]: RFailed(err) };
        throw new Error(err);
      }
    } else {
      return {};
    }
  };

  get allResponses() {
    return this._allResponses(this.responses);
  }
  private _allResponses = memoize((responses: { [year: string]: Resource<ResponseR[]> }) => {
    const allResponses = [];
    for (const year in responses) {
      const res = responses[year];
      if (res && res.type === "RDone") {
        allResponses.push(res.data);
      }
    }
    return ([] as ResponseR[]).concat.apply([], allResponses);
  });

  get root(): undefined | Root {
    return this._root(this.meta, this.responses);
  }
  private _root = memoize((meta: Resource<Meta>, responses: { [year: string]: Resource<ResponseR[]> }) => {
    if (meta.type !== "RDone") {
      return undefined;
    }

    const allResponses = [];
    for (const k in meta.data.years) {
      const res = responses[meta.data.years[k]];
      if (res && res.type === "RDone") {
        allResponses.push(res.data);
      }
    }

    return mkRoot(this.deployment, meta.data, ([] as ResponseR[]).concat.apply([], allResponses));
  });

  fetchData = (numYears: number) => {
    return this.fetchMeta().then(() => {
      if (this.meta && this.meta.type === "RDone") {
        const meta = this.meta.data;
        return Promise.all(meta.years.slice(0, numYears).map(this.fetchResponses)).then(() => {
          return {};
        });
      } else {
        throw new Error("Meta not loaded");
      }
    });
  };
}

export const newAtlasH = (deployment: Deployment, locale: string): AtlasH => new AtlasHImpl(deployment, locale);
